import React, { useState } from "react";
import { Form, Button, Alert, Spinner, Card } from "react-bootstrap";
import { useLanguage } from "../i18n/LanguageContext";

interface TwoFAInputProps {
  onVerify: (code: string) => Promise<void>;
  onBack: () => void;
  loading?: boolean;
}

const TwoFAInput: React.FC<TwoFAInputProps> = ({
  onVerify,
  onBack,
  loading = false,
}) => {
  const { t, dir } = useLanguage();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isBackupCode, setIsBackupCode] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!code.trim()) {
      setError(t("invalidCode"));
      return;
    }

    try {
      await onVerify(code);
    } catch (err: any) {
      setError(err.response?.data?.error || t("invalidCode"));
      setCode("");
    }
  };

  return (
    <Card
      className="shadow border-0"
      style={{ borderRadius: "16px" }}
      dir={dir}
    >
      <Card.Body className="p-4">
        <div className="text-center mb-4">
          <div style={{ fontSize: "48px" }}>🔐</div>
          <h4 className="mb-1" style={{ fontWeight: "600" }}>
            {t("twoFactorAuth")}
          </h4>
          <p className="text-muted small mb-0">{t("enterCode")}</p>
        </div>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError("")}
            className="py-2 small"
          >
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">
              {isBackupCode ? t("backupCode") : t("twoFactorAuth")}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={isBackupCode ? "XXXXXXXX" : "000000"}
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\s/g, "").toUpperCase())
              }
              maxLength={isBackupCode ? 8 : 6}
              disabled={loading}
              autoFocus
              style={{
                fontSize: "24px",
                textAlign: "center",
                letterSpacing: "0.3em",
                padding: "16px",
                borderRadius: "8px",
                fontWeight: "bold",
              }}
            />
          </Form.Group>

          <div className="d-grid gap-2 mb-3">
            <Button
              variant="primary"
              type="submit"
              size="lg"
              disabled={loading || !code.trim()}
              style={{
                padding: "12px",
                borderRadius: "8px",
                fontWeight: "600",
              }}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("loading")}
                </>
              ) : (
                t("submit")
              )}
            </Button>

            <Button
              variant="outline-secondary"
              onClick={() => setIsBackupCode(!isBackupCode)}
              disabled={loading}
            >
              {isBackupCode ? t("useAuthenticatorCode") : t("useBackupCode")}
            </Button>

            <Button
              variant="link"
              onClick={onBack}
              disabled={loading}
              className="text-muted"
            >
              ← {t("back")}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default TwoFAInput;
